import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, debounceTime, filter, firstValueFrom, Subject } from 'rxjs';

import { JOB_TYPES } from '../../../../../types';
import { jobCreationFlowToken } from '../../../Application/create-job-flow.provider';
import { CreateJobFlowStateModel } from '../../../Domain/Model/CreateJobFlow/create-job-flow-state.model';
import {
    StateKey,
    StateSchema,
    WebformCreateJobFlowState
} from '../../../Domain/Port/CreateJob/webform-create-job-flow.state';

/**
 * Implementation of WebformCreateJobFlowState that provides state persistence and restoration.
 *
 * This service:
 * - Stores job creation state in memory
 * - Persists state to permanent storage (via IonicStorage)
 * - Restores state from storage on initialization
 * - Ensures all getters wait for state restoration to complete before returning values
 * - Debounces state changes to minimize storage operations
 *
 * The service uses a BehaviorSubject to track when state restoration is complete,
 * ensuring that components don't receive null/default values during the restoration process.
 */
@Injectable({
    providedIn: 'root'
})
export class RestorableWebformCreateJobFlowStateService extends WebformCreateJobFlowState {
    /**
     * Subject that emits when any state property changes.
     * Used to trigger state persistence with debouncing.
     */
    private readonly stateChange$ = new Subject<void>();

    /**
     * Permanent storage service for persisting state between sessions.
     * Injected from the jobCreationFlowToken provider.
     */
    private readonly permanentStorage = inject(jobCreationFlowToken);

    /**
     * BehaviorSubject that tracks whether state is currently being restored.
     * Initially true, set to false when restoration is complete.
     * Used by getValue() to ensure values aren't accessed until restoration is complete.
     */
    private isStateRestoring = new BehaviorSubject<boolean>(true);

    /**
     * Constructor initializes the service by:
     * 1. Restoring state from permanent storage
     * 2. Setting up state change subscription to persist changes
     *
     * The service starts with isStateRestoring=true and sets it to false
     * only after state has been fully restored from storage.
     */
    constructor() {
        super();

        this.restoreState().then(() => {
            // Mark state restoration as complete
            this.isStateRestoring.next(false);

            // Set up subscription to save state changes with debouncing
            this.stateChange$.pipe(debounceTime(500)).subscribe(async () => {
                await this.saveState();
            });
        });
    }

    /**
     * Saves the current state to permanent storage.
     *
     * This method:
     * 1. Collects all current state values using getter methods
     * 2. Creates a complete state object
     * 3. Persists it to permanent storage
     *
     * Error handling ensures that storage failures don't crash the application.
     *
     * @returns Promise that resolves when state is saved
     */
    async saveState(): Promise<void> {
        try {
            const state: CreateJobFlowStateModel = {
                jobType: await this.jobType(),
                startDate: await this.startDate(),
                startTime: await this.startTime(),
                endDate: await this.endDate(),
                numberOfGuards: await this.numberOfGuards(),
                numberOfHoursPerDay: await this.numberOfHoursPerDay(),
                repeatCycle: await this.repeatCycle(),
                repeatCycleCustomType: await this.repeatCycleCustomType(),
                repeatDays: await this.repeatDays(),
                repeatCycleFrequencyAmount: await this.repeatCycleFrequencyAmount(),
                repeatEndDate: await this.repeatEndDate(),

                eventType: await this.eventType(),
                eventLocationAddress: await this.eventLocationAddress(),

                primaryContactName: await this.primaryContactName(),
                primaryContactPhone: await this.primaryContactPhone(),
                primaryContactEmail: await this.primaryContactEmail(),

                jobDescription: await this.jobDescription(),
                previousSecurityIssues: await this.previousSecurityIssues(),
                emergencyProcedures: await this.emergencyProcedures(),
                guardPrimaryDuties: await this.guardPrimaryDuties(),
                requireUniform: await this.requireUniform(),
                uniformType: await this.uniformType(),
                uniformProvidedBy: await this.uniformProvidedBy(),

                fromReviewScreen: await this.fromReviewScreen(),

                agreeToUPP: await this.agreeToUPP()
            };

            await this.permanentStorage.setItem(state);
        } catch (error) {
            console.error('Error saving job creation flow state:', error);
        }
    }

    /**
     * Restores the state from permanent storage.
     *
     * This method:
     * 1. Retrieves the saved state from permanent storage
     * 2. If state exists, sets all property values using setter methods
     * 3. Handles the case where no saved state exists
     *
     * During this process, isStateRestoring remains true, preventing
     * components from accessing potentially incomplete state.
     *
     * @returns Promise that resolves when state is restored
     */
    async restoreState(): Promise<void> {
        try {
            const state = await this.permanentStorage.getItem();
            if (!state) return;

            // Set all the values from the stored state
            await this.setJobType(state.jobType);
            await this.setStartDate(state.startDate);
            await this.setStartTime(state.startTime);
            await this.setEndDate(state.endDate);
            await this.setNumberOfGuards(state.numberOfGuards);
            await this.setNumberOfHoursPerDay(state.numberOfHoursPerDay);
            await this.setRepeatCycle(state.repeatCycle);
            await this.setRepeatCycleCustomType(state.repeatCycleCustomType);
            await this.setRepeatDays(state.repeatDays);
            await this.setRepeatCycleFrequencyAmount(state.repeatCycleFrequencyAmount);
            await this.setRepeatEndDate(state.repeatEndDate);

            await this.setEventType(state.eventType);
            await this.setEventLocationAddress(state.eventLocationAddress);

            await this.setPrimaryContactName(state.primaryContactName);
            await this.setPrimaryContactPhone(state.primaryContactPhone);
            await this.setPrimaryContactEmail(state.primaryContactEmail);

            await this.setJobDetails(state.jobDescription);
            await this.setPreviousSecurityIssues(state.previousSecurityIssues);
            await this.setEmergencyProcedures(state.emergencyProcedures);
            await this.setGuardPrimaryDuties(state.guardPrimaryDuties);
            await this.setRequireUniform(state.requireUniform);
            await this.setUniformType(state.uniformType);
            await this.setUniformProvidedBy(state.uniformProvidedBy);

            await this.setFromReviewScreen(state.fromReviewScreen);

            await this.setAgreeToUPP(state.agreeToUPP);
        } catch (error) {
            console.error('Error restoring job creation flow state:', error);
        }
    }

    /**
     * Clears all job creation data and removes it from permanent storage.
     *
     * This method:
     * 1. Resets all state properties to their default values
     * 2. Removes the saved state from permanent storage
     *
     * Used when a job creation flow is canceled or completed,
     * ensuring that a new job creation flow starts with a clean state.
     *
     * @returns Promise that resolves when all data is cleared
     */
    async clearJobCreation(): Promise<void> {
        await this.setJobType(JOB_TYPES.ONE_TIME);
        await this.setStartDate(null);
        await this.setStartTime(null);
        await this.setNumberOfGuards(null);
        await this.setNumberOfHoursPerDay(null);

        await this.setEventType(null);
        await this.setEventLocationAddress(null);

        await this.setPrimaryContactName(null);
        await this.setPrimaryContactEmail(null);
        await this.setPrimaryContactPhone(null);

        await this.setJobDetails(null);
        await this.setPreviousSecurityIssues(null);
        await this.setEmergencyProcedures(null);
        await this.setGuardPrimaryDuties(null);
        await this.setRequireUniform(null);
        await this.setUniformType(null);
        await this.setUniformProvidedBy(null);

        await this.setFromReviewScreen(false);

        await this.setAgreeToUPP(false);

        // Clear the data from permanent storage
        try {
            await this.permanentStorage.removeItem();
        } catch (error) {
            console.error('Error clearing job creation flow state from storage:', error);
        }
    }

    /**
     * Gets a value from the state by key.
     *
     * This method:
     * 1. Waits for state restoration to complete before returning any values
     * 2. Accesses the appropriate property using a naming convention (keyValue)
     * 3. Returns the value with proper typing
     *
     * The waiting mechanism ensures components don't receive null/default values
     * during the state restoration process.
     *
     * @param key The state property key to retrieve
     * @returns Promise resolving to the value of the specified property
     */
    protected override async getValue<K extends StateKey>(key: K): Promise<StateSchema[K]> {
        // Wait for state restoration to complete before returning values
        await firstValueFrom(this.isStateRestoring.pipe(filter(isRestoring => !isRestoring)));

        return super.getValue(key);
    }

    /**
     * Sets a value in the state by key.
     *
     * This method:
     * 1. Updates the in-memory property value
     * 2. Triggers the stateChange$ subject to initiate state persistence
     *
     * The stateChange$ subject is debounced to prevent excessive storage operations
     * when multiple properties are updated in quick succession.
     *
     * @param key The state property key to set
     * @param value The value to set
     * @returns Promise that resolves when the value is set
     */
    protected override async setValue<K extends StateKey>(key: K, value: StateSchema[K]): Promise<void> {
        await super.setValue(key, value);
        // Trigger state change notification (debounced in constructor)
        this.stateChange$.next();
    }
}
